import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AnnouncementsService from 'api/announcements/AnnouncementsService';
import { AnnouncementDto } from 'api/announcements/models/AnnouncementDto';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';

export const DISMISSED_STORAGE_KEY = 'ANNOUNCEMENTS_DISMISSED';
const REFETCH_INTERVAL_MS = 5 * 60 * 1000;
const MAX_BOUNDARY_DELAY_MS = 5 * 60 * 1000;
const BOUNDARY_PADDING_MS = 250;

interface AnnouncementsContextValue {
    active: AnnouncementDto[];
    visible: AnnouncementDto[];
    dismissedActiveCount: number;
    dismiss: (id: string) => void;
    restoreAll: () => void;
}

const AnnouncementsContext = createContext<AnnouncementsContextValue>({
    active: [],
    visible: [],
    dismissedActiveCount: 0,
    dismiss: () => undefined,
    restoreAll: () => undefined,
});

export const useAnnouncements = () => useContext(AnnouncementsContext);

const readDismissed = (): string[] => {
    try {
        const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};

const writeDismissed = (ids: string[]) => {
    try {
        localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(ids));
    } catch {
        // ignore storage errors
    }
};

type Props = {
    children: React.ReactNode;
};

const AnnouncementsProvider: React.FC<Props> = ({ children }: Props) => {
    const [active, setActive] = useState<AnnouncementDto[]>([]);
    const [dismissedIds, setDismissedIds] = useState<string[]>(readDismissed());
    const [nowMs, setNowMs] = useState<number>(() => Date.now());

    useEffect(() => {
        const list = Array.isArray(active) ? active : [];
        let nextBoundary = Infinity;
        for (const a of list) {
            const start = a.startsAt ? new Date(a.startsAt as any).getTime() : NaN;
            const end = a.endsAt ? new Date(a.endsAt as any).getTime() : NaN;
            if (!isNaN(start) && start > nowMs && start < nextBoundary) nextBoundary = start;
            if (!isNaN(end) && end > nowMs && end < nextBoundary) nextBoundary = end;
        }
        if (!isFinite(nextBoundary)) return;
        const delay = Math.min(MAX_BOUNDARY_DELAY_MS, Math.max(BOUNDARY_PADDING_MS, nextBoundary - Date.now() + BOUNDARY_PADDING_MS));
        const id = setTimeout(() => setNowMs(Date.now()), delay);
        return () => clearTimeout(id);
    }, [active, nowMs]);

    const fetchActive = useCallback(async () => {
        try {
            const result = await AnnouncementsService.getActive();
            setActive(Array.isArray(result) ? result : []);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get active announcements`, error);
        }
    }, []);

    useEffect(() => {
        fetchActive();
        const interval = setInterval(fetchActive, REFETCH_INTERVAL_MS);
        const onFocus = () => {
            setNowMs(Date.now());
            fetchActive();
        };
        window.addEventListener('focus', onFocus);
        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', onFocus);
        };
    }, [fetchActive]);

    const dismiss = useCallback((id: string) => {
        setDismissedIds(prev => {
            if (prev.includes(id)) {
                return prev;
            }
            const next = [...prev, id];
            writeDismissed(next);
            return next;
        });
    }, []);

    const restoreAll = useCallback(() => {
        setDismissedIds(prev => {
            const activeIds = (Array.isArray(active) ? active : []).map(a => a.id);
            const next = prev.filter(id => !activeIds.includes(id));
            writeDismissed(next);
            return next;
        });
    }, [active]);

    const safeActive = Array.isArray(active) ? active : [];

    const activeAndValid = useMemo(
        () => safeActive.filter(a => {
            const startTime = a.startsAt ? new Date(a.startsAt as any).getTime() : -Infinity;
            const endTime = a.endsAt ? new Date(a.endsAt as any).getTime() : Infinity;
            return startTime <= nowMs && endTime > nowMs;
        }),
        [safeActive, nowMs]
    );

    const visible = useMemo(
        () => activeAndValid.filter(a => !dismissedIds.includes(a.id)),
        [activeAndValid, dismissedIds]
    );

    const dismissedActiveCount = useMemo(
        () => activeAndValid.filter(a => dismissedIds.includes(a.id)).length,
        [activeAndValid, dismissedIds]
    );

    const value = useMemo<AnnouncementsContextValue>(
        () => ({ active: activeAndValid, visible, dismissedActiveCount, dismiss, restoreAll }),
        [activeAndValid, visible, dismissedActiveCount, dismiss, restoreAll]
    );

    return (
        <AnnouncementsContext.Provider value={value}>
            {children}
        </AnnouncementsContext.Provider>
    );
};

export default AnnouncementsProvider;
